import { useState } from 'react'
import styled from 'styled-components'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import useApi from '../../hooks/useApi'
import useTranslation from '../../hooks/useTranslation'
import useUserPermissions from '../../hooks/useUserPermissions'
import { authState } from '../../hooks/useAuth'
import { enterPreview, leavePreview, previewVersion } from '../../model/loadModel'

/**
 * The preview banner (30.03.08 §7), app-wide for the owner.
 *
 *   in a preview session   "You are previewing … real data"   [Leave preview]
 *   a preview exists       "A preview is ready"                [Try the preview]
 *
 * It cannot be dismissed while previewing: everything done in a preview is
 * done to real data, and the owner leaves Studio to try the change elsewhere,
 * so the warning goes with them. It is also the one place a browser session
 * enters or leaves a preview — Studio's Preview button publishes the preview
 * version on the server, and this switches the session onto it.
 *
 * Reads `model/status`, the record Studio's overview shows, under the same
 * query key, so a Preview, Discard or Deploy there updates it at once.
 */

const Bar = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 1rem;
    font-size: var(--font-S);
    color: #3d2a00;
    background: ${({ $active }) => ($active ? '#ffd166' : '#fff3cd')};
    border-bottom: 1px solid #e0b84a;
`

const Action = styled.button`
    padding: 0.25rem 0.9rem;
    border-radius: 0.375rem;
    border: 1px solid #8a6d1a;
    background: transparent;
    color: inherit;
    font-size: var(--font-S);
    cursor: pointer;
    &:disabled { opacity: 0.5; cursor: wait; }
`

const PreviewBanner = () => {
    const { isOwner } = useUserPermissions()
    const { getMe } = useApi()
    const { m, t } = useTranslation()
    const queryClient = useQueryClient()
    const [busy, setBusy] = useState(false)
    const inPreview = previewVersion()

    const { data } = useQuery({
        queryKey: ['model/status'],
        queryFn: () => getMe('model/status'),
        enabled: Boolean(isOwner),
        staleTime: 60 * 1000
    })
    // `{ data: record }`, the shape the overview card reads under the same key
    const ready = data?.data?.previewModelVersion

    if (!isOwner || (!inPreview && !ready)) return null

    const run = async (fn) => {
        setBusy(true)
        try {
            await fn(authState.data?.orgId)
            await queryClient.invalidateQueries()
        } finally {
            setBusy(false)
        }
    }

    if (inPreview) {
        return (
            <Bar $active role='status' data-testid='preview.banner'>
                <span>{m('all.preview.banner.active', [inPreview])}</span>
                <Action type='button' disabled={busy} onClick={() => run(leavePreview)} data-testid='preview.banner.leave'>
                    {t('all.preview.banner.leave')}
                </Action>
            </Bar>
        )
    }

    return (
        <Bar role='status' data-testid='preview.banner'>
            <span>{t('all.preview.banner.ready')}</span>
            <Action type='button' disabled={busy} onClick={() => run((orgId) => enterPreview(orgId, ready))} data-testid='preview.banner.enter'>
                {t('all.preview.banner.enter')}
            </Action>
        </Bar>
    )
}

export default PreviewBanner
